import { useEffect, useState } from 'react';
import { Sidebar } from './Sidebar';
import { Header } from './Header';

// Wraps every authenticated page with the sidebar + header.
// The sidebar is a slim icon rail that expands on hover (overlay) and can be
// pinned open; the spacer reserves the collapsed width (or full width when pinned).
export function AppShell({ title, children }) {
  const [pinned, setPinned] = useState(() => localStorage.getItem('sidebarPinned') === '1');
  const [hovered, setHovered] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const expanded = pinned || hovered;

  useEffect(() => {
    localStorage.setItem('sidebarPinned', pinned ? '1' : '0');
  }, [pinned]);

  return (
    <div className="min-h-screen">
      {/* Ambient "aurora" background — refined, premium depth: soft base, drifting
          brand blobs, a top-corner glow and a gentle vignette to frame the content. */}
      <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden bg-gradient-to-br from-[#f2f1fd] via-[#f6f4fd] to-[#efedfb] dark:from-[#0b1220] dark:via-[#0e1526] dark:to-[#0b1220]">
        <div className="absolute -top-44 -left-28 h-[36rem] w-[36rem] rounded-full bg-brand/20 dark:bg-brand/10 blur-3xl" style={{ animation: 'drift1 22s ease-in-out infinite' }} />
        <div className="absolute top-1/4 -right-24 h-[34rem] w-[34rem] rounded-full bg-accent/20 dark:bg-accent/15 blur-3xl" style={{ animation: 'drift2 27s ease-in-out infinite' }} />
        <div className="absolute -bottom-44 left-1/3 h-[34rem] w-[34rem] rounded-full bg-brand-light/20 dark:bg-brand-light/10 blur-3xl" style={{ animation: 'drift3 31s ease-in-out infinite' }} />
        <div className="absolute top-2/3 left-10 h-72 w-72 rounded-full bg-cyan-300/20 dark:bg-cyan-500/10 blur-3xl" style={{ animation: 'drift1 25s ease-in-out infinite' }} />
        {/* Top-right brand glow */}
        <div className="absolute inset-0" style={{ background: 'radial-gradient(120% 120% at 100% 0%, rgba(109,92,240,0.10) 0%, transparent 45%)' }} />
        {/* Soft vignette to keep focus on the content */}
        <div className="absolute inset-0" style={{ background: 'radial-gradient(130% 100% at 50% 40%, transparent 55%, rgba(15,23,42,0.05) 100%)' }} />
      </div>

      <Sidebar
        pinned={pinned} setPinned={setPinned}
        hovered={hovered} setHovered={setHovered}
        mobileOpen={mobileOpen} setMobileOpen={setMobileOpen}
      />

      {/* Mobile backdrop when the drawer is open. */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-slate-900/40 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Full width on mobile; shifts right on desktop when the rail is expanded. */}
      <div className={`relative z-10 flex min-h-screen min-w-0 flex-col transition-[margin] duration-200 ml-0 ${expanded ? 'lg:ml-60' : 'lg:ml-[72px]'}`}>
        <Header title={title} onMenu={() => setMobileOpen(true)} />
        <main className="min-w-0 flex-1 overflow-x-clip p-4 sm:p-6 space-y-6">{children}</main>
      </div>
    </div>
  );
}
